import { alpha, Avatar, AvatarGroup, Box, Chip, Typography, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import React, { ReactNode } from 'react'
import { Link } from 'react-router-dom'

// Icons for different field types
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import BadgeIcon from '@mui/icons-material/Badge'
import BusinessIcon from '@mui/icons-material/Business'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CampaignIcon from '@mui/icons-material/Campaign'
import CodeIcon from '@mui/icons-material/Code'
import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import GroupsIcon from '@mui/icons-material/Groups'
import InventoryIcon from '@mui/icons-material/Inventory'
import LanguageIcon from '@mui/icons-material/Language'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import NotesIcon from '@mui/icons-material/Notes'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import PublicIcon from '@mui/icons-material/Public'
import ShareIcon from '@mui/icons-material/Share'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import WorkIcon from '@mui/icons-material/Work'
import SvgIconComponent from '../../../images/SvgIconComponent'

export interface UniversalReadOnlyFieldProps {
  /** Field label */
  label: string
  /** Field name/key */
  name: string
  /** Field value */
  value: any
  /** The entity being displayed (organization, contact, etc.) */
  entity?: any
  /** Render context with lookup data */
  renderContext?: {
    users?: any[]
    contacts?: any[]
    organizations?: any[]
    existingCustomProducts?: any[]
    isParent?: boolean
  }
  /** Use enhanced modern styling */
  enhanced?: boolean
  /** Custom icon override */
  icon?: ReactNode
  /** Whether to auto-detect icon based on field name */
  autoIcon?: boolean

  // Legacy props for backwards compatibility
  contacts?: any[]
  users?: any[]
  organizations?: any[]
  organization?: any
  isParent?: boolean
  existingCustomProducts?: any[]
}

/**
 * Get icon based on field name
 */
const getFieldIcon = (name: string): ReactNode | null => {
  const iconMap: Record<string, ReactNode> = {
    // Person fields
    owner: <PersonIcon />,
    firstName: <BadgeIcon />,
    middleName: <BadgeIcon />,
    lastName: <BadgeIcon />,
    name: <PersonIcon />,
    fullName: <PersonIcon />,
    primaryContact: <PersonIcon />,
    billingContact: <PersonIcon />,
    contacts: <GroupsIcon />,
    additionalContacts: <GroupsIcon />,
    addtlContacts: <GroupsIcon />,
    team: <GroupsIcon />,
    team_details: <GroupsIcon />,
    // Team role fields
    'team.AM': <PersonIcon />,
    'team.PM': <PersonIcon />,
    'team.DMS': <PersonIcon />,
    'team.SEO': <PersonIcon />,
    'team.MIA': <PersonIcon />,
    'team.BDE': <PersonIcon />,
    'team.QA': <PersonIcon />,
    'team.other': <GroupsIcon />,

    // Organization fields
    organizationName: <BusinessIcon />,
    organizations: <BusinessIcon />,
    affiliated: <BusinessIcon />,
    affiliate: <BusinessIcon />,
    contractedBy: <BusinessIcon />,
    dba: <BusinessIcon />,
    legalEntityName: <BusinessIcon />,

    // Contact info
    phone: <PhoneIcon />,
    officePhone: <PhoneIcon />,
    mobilePhone: <PhoneIcon />,
    businessPhone: <PhoneIcon />,
    billingPhone: <PhoneIcon />,
    email: <EmailIcon />,
    billingEmail: <EmailIcon />,
    website: <LanguageIcon />,
    linkedinProfile: <LinkedInIcon />,
    social: <ShareIcon />,
    socialProfiles: <ShareIcon />,
    facebookAccess: <FacebookIcon />,

    // Location
    street: <LocationOnIcon />,
    city: <LocationOnIcon />,
    state: <LocationOnIcon />,
    zip: <LocationOnIcon />,
    address: <LocationOnIcon />,
    geoTargeting: <PublicIcon />,

    // Date fields
    clientStartDate: <CalendarTodayIcon />,
    averageTenure: <CalendarTodayIcon />,
    createdAt: <CalendarTodayIcon />,
    updatedAt: <CalendarTodayIcon />,

    // Business fields
    title: <WorkIcon />,
    type: <BadgeIcon />,
    cmsUsed: <CodeIcon />,
    marketingObjective: <CampaignIcon />,
    productsAndServices: <ShoppingCartIcon />,

    // Other
    summary: <NotesIcon />,
    notes: <NotesIcon />,
    budget: <AttachMoneyIcon />,
    __parentTotals__: <AttachMoneyIcon />,
    revenue: <AttachMoneyIcon />,
    custom_selection: <InventoryIcon />,
  }

  return iconMap[name] || null
}

/**
 * Format phone number for display
 */
const formatPhoneNumber = (phone: string): string => {
  if (!phone) return ''
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

/**
 * Format date with days since
 */
const formatDateWithDays = (dateValue: string | Date): string => {
  if (!dateValue) return ''
  const date = dayjs(dateValue)
  const daysSince = dayjs().diff(date, 'day')
  return `${date.format('MM/DD/YYYY')} - ${daysSince} days`
}

/**
 * Get item name from various object shapes
 */
const getItemName = (item: any): string =>
  item?.name || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || item?.real_name || item?.email || 'Unknown'

/**
 * Format currency for display
 */
const formatCurrency = (value: number): string => {
  if (value === null || value === undefined) return '$0.00'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * UniversalReadOnlyField - A comprehensive field display component
 * that handles rendering for Organizations, Contacts, and other entities
 *
 * Features:
 * - Auto-detected icons based on field name
 * - Smart rendering for contacts, users, organizations references
 * - Phone number formatting and click-to-call
 * - Email mailto links
 * - Website and LinkedIn external links
 * - Date formatting with "days since"
 * - User/Team avatar rendering
 * - Subtle hover effects
 * - Clean typography hierarchy
 */
const UniversalReadOnlyField: React.FC<UniversalReadOnlyFieldProps> = ({
  label,
  name,
  value,
  entity,
  renderContext = {},
  enhanced = true,
  icon,
  autoIcon = true,
  // Legacy props
  contacts: legacyContacts,
  users: legacyUsers,
  organizations: legacyOrganizations,
  organization: legacyOrganization,
  isParent: legacyIsParent,
  existingCustomProducts: legacyCustomProducts,
}) => {
  const theme = useTheme()

  // Merge renderContext with legacy props
  const contacts = renderContext.contacts ?? legacyContacts ?? []
  const users = renderContext.users ?? legacyUsers ?? []
  const organizations = renderContext.organizations ?? legacyOrganizations ?? []
  const currentEntity = entity ?? legacyOrganization
  const isParent = renderContext.isParent ?? legacyIsParent ?? false
  const existingCustomProducts = renderContext.existingCustomProducts ?? legacyCustomProducts ?? []

  // Get icon for this field
  const displayIcon = icon ?? (autoIcon && enhanced ? getFieldIcon(name) : null)

  // Check if value is empty
  const isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

  // Styles
  const labelStyle = enhanced
    ? {
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        mb: 0.25,
      }
    : {
        color: theme.palette.text.secondary,
        fontWeight: 500,
        fontSize: '0.875rem',
      }

  const valueStyle = {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '0.95rem',
    lineHeight: 1.5,
  }

  const emptyStyle = {
    color: alpha(theme.palette.text.secondary, 0.5),
    fontWeight: 400,
    fontSize: '0.9rem',
    fontStyle: 'italic' as const,
  }

  const linkStyle = {
    ...valueStyle,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline',
    },
  }

  /**
   * Render user avatars (for Owner, Team fields)
   */
  const renderUserAvatars = (userIds: any[], showNames = true) => {
    const matchedUsers = (Array.isArray(userIds) ? userIds : [userIds])
      .map(id => {
        if (typeof id === 'object' && id?.user_uuid) {
          return users.find((u: any) => u?.user_uuid === id.user_uuid)
        }
        return users.find((u: any) => u?.user_uuid === id || u?.id === id)
      })
      .filter(Boolean)

    if (matchedUsers.length === 0) {
      return <Typography sx={emptyStyle}>Not assigned</Typography>
    }

    if (enhanced && showNames && matchedUsers.length <= 2) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {matchedUsers.map((user: any) => (
            <Box key={user.id || user.user_uuid} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Avatar alt={getItemName(user)} src={user?.image_512 || user?.photoUrl} sx={{ width: 28, height: 28 }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{getItemName(user)}</Typography>
            </Box>
          ))}
        </Box>
      )
    }

    return (
      <AvatarGroup max={6} spacing={4} sx={{ justifyContent: 'flex-start' }}>
        {matchedUsers.map((user: any) => (
          <Avatar
            key={user.id || user.user_uuid}
            alt={getItemName(user)}
            src={user?.image_512 || user?.photoUrl}
            sx={{ width: 32, height: 32 }}
          />
        ))}
      </AvatarGroup>
    )
  }

  /**
   * Render contact references (for Primary Contact, Billing Contact, etc.)
   */
  const renderContactReferences = (contactIds: any[]) => {
    const ids = Array.isArray(contactIds) ? contactIds : [contactIds]
    const matchedContacts = ids
      .map(id => contacts.find((c: any) => c?.contacts_uuid === id || c?.id === id))
      .filter(Boolean)

    if (matchedContacts.length === 0) {
      return <Typography sx={emptyStyle}>Not assigned</Typography>
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {matchedContacts.map((contact: any) => (
          <Link
            key={contact.contacts_uuid || contact.id}
            to={`/contacts/${contact.contacts_uuid || contact.id}/details`}
            style={{ textDecoration: 'none' }}
          >
            <Typography sx={linkStyle}>{getItemName(contact)}</Typography>
          </Link>
        ))}
      </Box>
    )
  }

  /**
   * Render organization references (for Affiliate, Contracted By, etc.)
   */
  const renderOrganizationReferences = (orgIds: any[]) => {
    const ids = Array.isArray(orgIds) ? orgIds : [orgIds]

    // Normalize IDs
    const normalizedIds = ids
      .map((v: any) => {
        if (typeof v === 'string') return v.replace(/^ORG_/, '')
        if (typeof v === 'number') return String(v)
        return v?.org_uuid ?? v?.id ?? ''
      })
      .filter(Boolean)

    const matchedOrgs = organizations.filter((o: any) => {
      const orgId = o?.org_uuid ?? o?.id ?? ''
      return normalizedIds.includes(String(orgId).replace(/^ORG_/, ''))
    })

    if (matchedOrgs.length === 0) {
      return <Typography sx={emptyStyle}>Not assigned</Typography>
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {matchedOrgs.map((org: any) => {
          const orgIdForRoute = org?.org_uuid ?? org?.id ?? ''
          return (
            <Link
              key={org.org_uuid || org.id}
              to={`/organizations/ORG_${orgIdForRoute}/details`}
              style={{ textDecoration: 'none' }}
            >
              <Typography sx={linkStyle}>{org.name || 'Unknown Organization'}</Typography>
            </Link>
          )
        })}
      </Box>
    )
  }

  /**
   * Main render value logic
   */
  const renderValue = () => {
    // Already a React element - render as-is
    if (React.isValidElement(value)) {
      return value
    }

    // Parent Totals field - render summary with currency formatting
    if (name === '__parentTotals__' && value && typeof value === 'object') {
      const totals = value as { allTotal?: number; activeRunningTotal?: number; productCount?: number }
      return (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
              Active Totals (running today)
            </Typography>
            <Typography sx={{ ...valueStyle, fontSize: '1.1rem', fontWeight: 600 }}>
              {formatCurrency(totals.activeRunningTotal ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
              Total (all products)
            </Typography>
            <Typography sx={{ ...valueStyle, fontSize: '1.1rem', fontWeight: 600 }}>
              {formatCurrency(totals.allTotal ?? 0)}
            </Typography>
          </Box>
          {typeof totals.productCount === 'number' && (
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                Products
              </Typography>
              <Typography sx={{ ...valueStyle, fontSize: '1.1rem', fontWeight: 600 }}>{totals.productCount}</Typography>
            </Box>
          )}
        </Box>
      )
    }

    // Custom Selection field - render product items for parent organizations
    if (name === 'custom_selection' && isParent && existingCustomProducts?.length > 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {existingCustomProducts.map((product: any) => (
            <Box
              key={product?.SK || product?.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 0.75,
                px: 1.5,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }} noWrap>
                  {product?.title || product?.productName || 'Unnamed Product'}
                </Typography>
                {product?.category_name || product?.category ? (
                  <Chip
                    label={product?.category_name || product?.category}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    }}
                  />
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  {product?.price ? formatCurrency(parseFloat(product.price)) : '$0.00'}
                </Typography>
                {product?.margin && (
                  <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                    {Math.round(parseFloat(product.margin))}%
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )
    }

    // Empty value
    if (isEmpty) {
      return <Typography sx={emptyStyle}>Not specified</Typography>
    }

    // Owner field - user avatars
    if (name === 'owner') {
      return renderUserAvatars(value, true)
    }

    // Team field - user avatars
    if (name === 'team' || name === 'team_details') {
      return renderUserAvatars(value, false)
    }

    // Team role fields (team.AM, team.PM, etc.) - user avatars
    if (name.startsWith('team.')) {
      return renderUserAvatars(value, true)
    }

    // Contact reference fields
    if (['primaryContact', 'billingContact', 'contacts', 'additionalContacts', 'addtlContacts'].includes(name)) {
      return renderContactReferences(value)
    }

    // Organization reference fields
    if (['affiliated', 'affiliate', 'contractedBy', 'organizations'].includes(name)) {
      return renderOrganizationReferences(value)
    }

    // Phone fields - formatted and clickable
    if (['phone', 'officePhone', 'mobilePhone', 'businessPhone', 'billingPhone'].includes(name) && value) {
      const formatted = formatPhoneNumber(String(value))
      return (
        <a href={`tel:${value}`} style={{ textDecoration: 'none' }}>
          <Typography sx={linkStyle}>{formatted}</Typography>
        </a>
      )
    }

    // Email fields - mailto link
    if (['email', 'billingEmail'].includes(name) && value) {
      return (
        <a href={`mailto:${value}`} style={{ textDecoration: 'none' }}>
          <Typography sx={linkStyle}>{value}</Typography>
        </a>
      )
    }

    // Website field - external link
    if (name === 'website' && value) {
      const url = String(value).startsWith('http') ? value : `https://${value}`
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <Typography sx={linkStyle}>{value}</Typography>
        </a>
      )
    }

    // LinkedIn field - external link
    if (name === 'linkedinProfile' && value) {
      const url = String(value).startsWith('http') ? value : `https://${value}`
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <Typography sx={linkStyle}>{value}</Typography>
        </a>
      )
    }

    // Client Start Date - formatted with days
    if (name === 'clientStartDate' && value) {
      return <Typography sx={valueStyle}>{formatDateWithDays(value)}</Typography>
    }

    // Average Tenure - calculate from entity
    if (name === 'averageTenure') {
      const startDate = currentEntity?.clientStartDate ?? currentEntity?.creation_timestamp
      if (startDate) {
        const daysActive = dayjs().diff(dayjs(startDate), 'day')
        return <Typography sx={valueStyle}>{daysActive} days</Typography>
      }
      return <Typography sx={emptyStyle}>Not available</Typography>
    }

    // Social profiles - render icon component
    if ((name === 'social' || name === 'socialProfiles') && Array.isArray(value)) {
      return <SvgIconComponent socialProfiles={value} isReadOnlyRender={true} />
    }

    // Default: arrays join, strings display
    if (Array.isArray(value)) {
      return <Typography sx={valueStyle}>{value.length > 0 ? value.join(', ') : 'Not specified'}</Typography>
    }

    return <Typography sx={valueStyle}>{String(value)}</Typography>
  }

  // Enhanced layout with icon
  if (enhanced && displayIcon) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {/* Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            flexShrink: 0,
            mt: 0.25,
            '& svg': {
              fontSize: '1.1rem',
              color: theme.palette.primary.main,
            },
          }}
        >
          {displayIcon}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={labelStyle}>{label}</Typography>
          {renderValue()}
        </Box>
      </Box>
    )
  }

  // Standard layout (no icon or not enhanced)
  return (
    <Box>
      <Typography sx={labelStyle}>{label}</Typography>
      {renderValue()}
    </Box>
  )
}

export default UniversalReadOnlyField
